import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { createSlugToIdMapping } from '../utils/slugUtils';
import { PROD_ENDPOINTS, REVIEW_ENDPOINTS } from '../constants/api';

interface UseProjectBySlugResult {
  project: Project | undefined;
  loading: boolean;
  error: string | null;
}

const useProjectBySlug = (
  slug: string
): UseProjectBySlugResult => {
  const [project, setProject] = useState<
    Project | undefined
  >();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadProjectBySlug = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, we need to get the list of projects to create slug-to-ID mapping
      // We'll fetch the first page with a large number to get all projects for mapping
      const projectsResponse = await axios.post(
        PROD_ENDPOINTS.CASCADE_FILTER,
        {
          numberItems: '1300', // Large number to get all projects
          page: '1',
          search: '',
          type: [],
          countries: [],
          useCase: [],
          technology: [],
          theme: [],
          users: [],
          businessModel: [],
          fvc: [],
          maturity: [],
          organizationType: [],
          fourBetters: [],
          sdg: [],
          countryOfOrigin: [],
        }
      );

      if (projectsResponse.status === 200) {
        const { data: initiatives } =
          projectsResponse.data as AnalyticsResponse;

        // Create slug-to-ID mapping
        const slugToIdMapping =
          createSlugToIdMapping(initiatives);

        // Find project ID by slug
        const projectId = slugToIdMapping[slug];

        if (!projectId) {
          console.log('Project not found for slug:', slug, slugToIdMapping);
          setError(`Project not found for slug: ${slug}`);
          setLoading(false);
          return;
        }

        // Now fetch the full project details by ID
        const projectResponse = await axios.get(
          PROD_ENDPOINTS.GET_PROJECT_BY_ID,{
            params: { id: projectId },
          }
        );

        if (projectResponse.status === 200) {
          setProject(projectResponse.data);
        } else {
          setError('Failed to fetch project details');
        }
      } else {
        console.log('Failed to fetch projects list:');
        setError('Failed to fetch projects list');
      }
    } catch (err) {
      console.error('Error loading project by slug:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unknown error occurred'
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadProjectBySlug();
  }, [loadProjectBySlug]);

  return { project, loading, error };
};

export default useProjectBySlug;
